<template>
  <span id="add-manual-invoice-detail">
    <el-button
      id="add-manual-invoice-detail-button"
      class="button-action-default"
      size="mini"
      type="success"
      plain
      @click="dialogVisible = true"
    >{{ $t('invoices.addInvoiceDetail') }}</el-button>

    <el-dialog
      id="add-storage-location-dialog"
      v-on:open="handleOpen"
      :title="$t('invoices.addInvoiceDetail')"
      :visible.sync="dialogVisible"
      width="50%"
      class="dialog-with-cols"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form :model="form" :rules="formRules" ref="form" label-position="top" size="mini">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item :label="$t('invoices.details.chargeCode')" prop="chargeCode">
               <el-select
                       filterable
                       v-model="chargeSelected"
                       value-key="Name"
                       :loading="loading"
                       id="form-charge-select"
                       ref="charge-select"
                       @change="selectCharge"
               >
                <el-option
                        v-for="charge in chargeList"
                        :key="charge.Name"
                        :label="charge.Code__c && charge.Name ? `${charge.Code__c} - ${charge.Name}`: ''"
                        :value="charge"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="16">
            <el-form-item :label="$t('invoices.details.description')" prop="description">
              <el-input
                class="inline-input"
                v-model="form.description"
                v-alphanumeric-validation
                maxlength="100"
                clearable
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item :label="$t('invoices.details.rate') + (form.unitOfMeasure === unitOfMeasureOptions.PERCENTAGE ? $t('invoices.details.percentage') : $t('invoices.details.dollar'))" prop="rate">
               <el-input
                       v-on:input="calculateAmount"
                       class="inline-input"
                       v-model="form.rate"
                       v-numeric-allow-negative-validation
                       maxlength="10"
                       clearable
               ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item :label="$t('invoices.details.quantity') + (form.unitOfMeasure === unitOfMeasureOptions.PERCENTAGE ? $t('invoices.details.dollar') : '')" prop="quantity">
               <el-input
                       v-on:input="calculateAmount"
                       class="inline-input"
                       v-model="form.quantity"
                       v-numeric-validation
                       maxlength="10"
                       clearable
               ></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item :label="$t('invoices.details.amount')" prop="amount">
               <el-input
                       class="inline-input"
                       v-model="form.amount"
                       v-alphanumeric-validation
                       maxlength="10"
                       clearable
                       disabled
               ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div
        id="add-storage-location-dialog-footer"
        slot="footer"
        class="dialog-footer form-vertical-item flex-reverse-direction"
      >
        <el-button
          type="primary"
          id="save-storage-location-button"
          @click="submitForm()"
          size="mini"
        >{{ $t('invoices.addInvoiceDetail') }}</el-button>
        <el-button
          id="refresh-storage-location-button"
          @click="resetForm()"
          size="mini"
        >{{ $t('common.clear') }}</el-button>
      </div>
    </el-dialog>
  </span>
</template>

<script>
import { Message } from 'element-ui';
import constants from '@/utils/constants';
import apiConstants from '@/utils/apiConstants';
import selectService from '@/api/selectService';

export default {
  name: 'AddManualInvoiceDetail',
  props: {
    invoice: {
      type: Object,
      require: true
    },
  },
  data() {
    return {
      chargeList: [],
      chargeSelected: { Name: null },
      dialogVisible: false,
      loading: false,
      form: {
        chargeCode: null,
        chargeCodeName: null,
        description: null,
        unitOfMeasure: null,
        rate: null,
        quantity: null,
        amount: null,
        invoiceType: constants.INVOICE.TYPES[2].value, // manual invoice
        invoiceId: null
      },
      formRules: {
        chargeCode: [
          {
            required: true,
            message: this.$t('invoices.manual.error.chargeCodeRequired'),
            trigger: 'blur'
          }
        ],
        description: [
          {
            required: true,
            message: this.$t('invoices.manual.error.descriptionRequired'),
            trigger: 'blur'
          }
        ],
        rate: [
          {
            required: true,
            message: this.$t('invoices.manual.error.rateRequired'),
            trigger: 'blur'
          }
        ],
        quantity: [
          {
            required: true,
            message: this.$t('invoices.manual.error.quantityRequired'),
            trigger: 'blur'
          }
        ]
      },
      rateType: constants.SHIPMENT.RATE_TYPE,
      unitOfMeasureOptions: constants.INVOICE.RATE_UNIT_OF_MEASURE
    };
  },
  methods: {
    handleOpen() {
      this.form.invoiceId = this.invoice.id;
      this.searchChargeCodes();
      this.$nextTick(() => {
        this.resetForm();
      });
    },
    searchChargeCodes() {
      this.chargeList = [{}];
      this.loading = true;
      selectService.getList(apiConstants.END_POINTS.BILLING.SALESFORCE_CHARGES).then((response) => {
        if (response instanceof Array) {
          this.chargeList = this.chargeList.concat(response);
          this.loading = false;
        }
      }).catch(() => {
        this.loading = false;
      });
    },
    selectCharge(charge) {
      this.chargeSelected = charge;
      this.form.chargeCode = charge.Code__c;
      this.form.chargeCodeName = charge.Name;
      this.form.description = charge.Name;
      this.form.chargeType = charge.Charge_Type__c;
      this.form.glCode = charge.GL_Code__c;
      this.form.revenue = charge.Revenue_Charge__c;
      this.form.unitOfMeasure = charge.Units_of_Measure__c || '';
      this.calculateAmount();
    },
    calculateAmount() {
      if (this.form.rate && this.form.quantity && this.chargeSelected) {
        if (this.chargeSelected.Charge_Type__c === this.rateType.UNIT.value
                || this.chargeSelected.Charge_Type__c === this.rateType.CUBE.value) {
          // UNITS or CUBES
          this.form.amount = this.form.quantity * this.form.rate;
        } else if (this.chargeSelected.Charge_Type__c === this.rateType.NON_REVENUE.value) {
          // NON_REVENUE
          this.form.amount = this.form.quantity * (this.form.rate / 100);
        } else {
          this.form.amount = 0;
        }
      } else {
        this.form.amount = null;
      }
    },
    submitForm() {
      this.$refs.form.validate(async (valid, validationsErrors) => {
        if (valid) {
          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          this.form.quantity = Number(this.form.quantity);
          this.form.rate = Number(this.form.rate);
          this.form.invoiceId = this.invoice.id;
          this.$store
            .dispatch('invoices/addDetails', this.form)
            .then((response) => {
              this.$message.success(response.message);
              this.invoice.totalAmount = response.data.totalAmount;
              this.dialogVisible = false;
              this.resetForm();
              loading.close();
              this.$emit('updateDetails');
            })
            .catch((error) => {
              this.$message.error(error.response.message);
              loading.close();
            });
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}. `;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    },
    resetForm() {
      this.chargeSelected = { Name: null };
      this.form.description = null;
      this.form.unitOfMeasure = null;
      this.form.rate = null;
      this.form.quantity = null;
      this.form.chargeCode = '';
      this.form.chargeCodeName = '';
      this.form.type = '';
      this.form.glCode = '';
      this.form.revenue = false;
      this.form.amount = null;
      this.$refs.form.resetFields();
    }
  }
};
</script>
